import { useState, useEffect, useRef, useCallback } from "react";

export function useInfiniteScroll({ fetchFn, pageSize = 10, resetKey }) {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  // console.log("resetKeys:", resetKey, hasMore);

  const sentinelRef = useRef(null);
  const containerRef = useRef(null);

  const isFetchingRef = useRef(false);

  // useEffect(() => {
  //   setItems([]);
  //   setPage(1);
  //   setHasMore(true);
  // }, [resetKey]);

  const fetchData = useCallback(async (targetPage, isReset = false) => {
    if (loading) return;
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const { data, total } = await fetchFn({
        page:targetPage,
        pageSize,
      });

      setItems((prev) => {
        const updated = isReset ? data : [...prev, ...data];
        setHasMore(updated.length < total);
        return updated;
      });

      setPage(targetPage + 1);
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [fetchFn, pageSize, loading]);

  //Initial call should be not with intersection observer and reset key should trigger the api again
  useEffect(() => {
    setItems([]);
    setPage(1);
    setHasMore(true);
    console.log("reset key useEffect");
    fetchData(1, true);
  }, [resetKey]);

  //load more are called by intersection observer
  const loadMore = useCallback(() => {
    if (!isFetchingRef.current  && hasMore && !error) {
      fetchData(page, false);
    }
  }, [fetchData, page, loading, hasMore, error]);

  //Manual refresh function
  const refresh = useCallback(() => {
    setPage(1);
    setHasMore(true);
    fetchData(1, true);
  }, [fetchData]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !isFetchingRef.current) {
          loadMore();
        }
      },
      {
        root: containerRef.current,
        threshold: 0.1,
        rootMargin: "100px",
      },
    );

    const current = sentinelRef.current;

    if (current) {
      observer.observe(current);
    }

    return () => {
      if (current) observer.unobserve(current);
    };
  }, [loadMore, hasMore]);

  return {
    items,
    loading,
    hasMore,
    error,
    sentinelRef,
    containerRef,
    setItems,
    refresh,
  };
}
